'use client';
import { Typography, Button, Stack, Box, Tag, Divider } from '@castlery/fortress';
import { User } from '@castlery/types';
import { ChevronRight, Star } from '@castlery/fortress/Icons';
import { formatDate } from '@castlery/utils';
import { CustomLink } from '@castlery/shared-components';

interface AccountDetailsProps {
  userInfo?: User | null;
  onEditAccount?: () => void;
  onUpdatePassword?: () => void;
  onEditProfile?: () => void;
  customerSubscription?: {
    email: boolean;
    sms: boolean;
  };
}

export function AccountDetails({
  userInfo,
  customerSubscription,
  onEditAccount,
  onUpdatePassword,
  onEditProfile,
}: AccountDetailsProps) {
  if (!userInfo) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Stack spacing={8}>
      <Stack spacing={6}>
        <Typography level="h2">My Account</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              // xs: '1fr',
              xs: '1fr 1fr',
              sm: '1fr 1fr',
            },
            gap: {
              xs: 3,
              sm: 4,
            },
            rowGap: {
              xs: 4,
              sm: 4,
            },
            '& .account-field': {
              display: 'flex',
              flexDirection: 'column',
            },
            '& .account-buttons': {
              gridColumn: {
                xs: '1',
                sm: '1 / -1', // 跨所有列
              },
            },
          }}
        >
          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Name
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.firstname} {userInfo.lastname}
            </Typography>
          </Box>

          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Password
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              ••••••••
            </Typography>
          </Box>

          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Email
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.email}
            </Typography>
          </Box>

          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Phone Number
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.phone || '-'}
            </Typography>
          </Box>

          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Email Subscription
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {customerSubscription?.email ? 'Active' : 'Inactive'}
            </Typography>
          </Box>

          <Box className="account-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Mobile Subscription
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.phone ? (customerSubscription?.sms ? 'Active' : 'Inactive') : '-'}
            </Typography>
          </Box>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={6}>
          <Button variant="outlined" color="neutral" onClick={onEditAccount}>
            EDIT ACCOUNT DETAILS
          </Button>
          <Button variant="outlined" color="neutral" onClick={onUpdatePassword}>
            UPDATE PASSWORD
          </Button>
        </Stack>
      </Stack>

      {/* Profile Details Section */}

      <Stack spacing={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Typography level="h2">My Profile</Typography>
          {userInfo.profile?.birthday === '' && (
            <Tag
              variant="solid"
              startDecorator={<Star />}
              endDecorator={<ChevronRight />}
              onClick={onEditProfile}
              sx={{
                cursor: 'pointer',
              }}
            >
              <Typography level="caption1">Receive a yearly reward on us!</Typography>
            </Tag>
          )}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              // xs: '1fr',
              xs: '1fr 1fr',
              sm: '1fr 1fr',
            },
            gap: {
              xs: 3,
              sm: 4,
            },
            rowGap: {
              xs: 4,
              sm: 6,
            },
            '& .profile-field': {
              display: 'flex',
              flexDirection: 'column',
            },
            '& .profile-button': {
              gridColumn: {
                xs: '1',
                sm: '1 / -1', // 跨所有列
              },
            },
          }}
        >
          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Date of Birth
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.birthday ? formatDate(userInfo.profile?.birthday, 'MMM yyyy') : '-'}
            </Typography>
          </Box>

          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Occupation
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.occupation || '-'}
            </Typography>
          </Box>

          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Housing Type
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.housing_type || '-'}
            </Typography>
          </Box>

          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Home Size
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.home_size || '-'}
            </Typography>
          </Box>

          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Most Time Spent In
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.most_time_spent_location || '-'}
            </Typography>
          </Box>

          <Box className="profile-field">
            <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
              Budget and Finances
            </Typography>
            <Typography level="body1" sx={{ py: 4 }}>
              {userInfo.profile?.annual_household_income || '-'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          color="neutral"
          onClick={onEditProfile}
          sx={{
            width: {
              xs: '100%',
              sm: 'fit-content',
              md: 'fit-content',
            },
          }}
        >
          EDIT PROFILE DETAILS
        </Button>
      </Stack>

      <Box>
        <Divider sx={{ mb: 6 }} />
        <Typography level="body2" sx={{ color: 'var(--fortress-palette-brand-mono-700)' }}>
          The birthday reward is an annual reward and will only be awarded to The Castlery Club members who have made at
          least 1 purchase before and have given their birthday information at least 7 days prior to the start of the
          month. All standard <CustomLink linkKey="promo-terms">Terms and Conditions</CustomLink> apply.
        </Typography>
      </Box>
    </Stack>
  );
}
